import { IPagedCollection } from '@moko/utils';
import { FilterOperator } from '@moko/ui';
import { internalFetch } from '../utils/fetchHelper';

export type AccessRuleEntity = 'Work' | 'Order';

export interface IAccessRulePermission {
  userId?: number | null;
  groupId?: number | null;
  tenantId?: number | null;
}

export interface IAccessRule {
  id: number;
  metadataName?: string | null;
  fieldName: string;
  operator: FilterOperator;
  fieldValue: string;
  permissions?: IAccessRulePermission[] | null;
  tenantId?: number | null;
}

export interface IAccessRuleMutation {
  metadataName?: string | null;
  fieldName: string;
  operator: FilterOperator;
  fieldValue: string;
  permissions: IAccessRulePermission[];
  tenantId?: number | null;
}

const path = (entity: AccessRuleEntity, suffix: string): string => {
  const base = entity === 'Work' ? 'work' : 'order';
  return `${base}${suffix}`;
};

const apiUrl = (): string => import.meta.env.VITE_BACKEND_API_URL as string;
const apiKey = (): string => import.meta.env.VITE_BACKEND_API_KEY as string;

export const getAccessRules = async (
  abortController: AbortController,
  entity: AccessRuleEntity,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string,
  filters?: string
): Promise<IPagedCollection<IAccessRule>> => {
  const qs = new URLSearchParams();
  qs.set('pageSize', String(pageSize));
  qs.set('pageNo', String(pageNo));
  if (orderBy) qs.set('orderBy', orderBy);
  if (search) qs.set('search', search);
  if (filters) qs.set('filters', filters);
  qs.set('allTenants', 'true');
  qs.set('code', apiKey());
  return internalFetch(
    `${apiUrl()}/p/${path(entity, 'AccessRules')}?${qs}`,
    'GET',
    abortController
  );
};

export const getAccessRule = async (
  abortController: AbortController,
  entity: AccessRuleEntity,
  ruleId: number
): Promise<IAccessRule> => {
  return internalFetch(
    `${apiUrl()}/p/${path(
      entity,
      'AccessRule'
    )}?ruleId=${ruleId}&code=${apiKey()}`,
    'GET',
    abortController
  );
};

export const createAccessRule = async (
  abortController: AbortController,
  entity: AccessRuleEntity,
  data: IAccessRuleMutation
): Promise<{ ruleId: number }> => {
  return internalFetch(
    `${apiUrl()}/p/${path(entity, 'AccessRule')}`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const updateAccessRule = async (
  abortController: AbortController,
  entity: AccessRuleEntity,
  ruleId: number,
  data: IAccessRuleMutation
): Promise<void> => {
  return internalFetch(
    `${apiUrl()}/p/${path(entity, 'AccessRule')}?ruleId=${ruleId}`,
    'PUT',
    abortController,
    JSON.stringify(data)
  );
};

export const deleteAccessRule = async (
  abortController: AbortController,
  entity: AccessRuleEntity,
  ruleId: number
): Promise<void> => {
  return internalFetch(
    `${apiUrl()}/p/${path(entity, 'AccessRule')}?ruleId=${ruleId}`,
    'DELETE',
    abortController
  );
};
